<template>
  <div class="share-avatar-badge" :style="badgeStyle">
    <template v-if="displayMode === 'text'">
      <span class="badge-text" :style="{ color: textColor }">{{ displayText }}</span>
    </template>
    <img v-else-if="displayMode === 'image'" :src="imageUrl" crossorigin="anonymous" class="badge-image"
      alt="badge-avatar">
    <span v-else class="badge-text">--</span>
  </div>
</template>

<script setup>
const props = defineProps({
  shortName: {
    type: String,
    default: '',
  },
  imageUrl: {
    type: String,
    default: '',
  },
  size: {
    type: Number,
    default: 28,
  },
})

const palette = [
  '#2F80ED',
  '#27AE60',
  '#F2994A',
  '#EB5757',
  '#9B51E0',
  '#56CCF2',
  '#F2C94C',
  '#6FCF97',
]

const normalizedName = computed(() => String(props.shortName || '').trim())

const displayText = computed(() => {
  const source = normalizedName.value
  if (!source) return '--'
  const words = source.split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
})

const displayMode = computed(() => {
  if (normalizedName.value) return 'text'
  if (props.imageUrl) return 'image'
  return 'text'
})

const bgColor = computed(() => {
  if (!normalizedName.value) return '#A9B4C6'
  let hash = 0
  for (let i = 0; i < displayText.value.length; i += 1) {
    hash = (hash * 31 + displayText.value.charCodeAt(i)) % palette.length
  }
  return palette[hash]
})

const badgeStyle = computed(() => {
  const size = `${props.size}px`
  return {
    width: size,
    height: size,
    background: displayMode.value === 'text' ? bgColor.value : '#FFFFFF',
  }
})

function hexToRgb(hex = '') {
  const normalized = String(hex).replace('#', '')
  if (normalized.length !== 6) return null
  const num = Number.parseInt(normalized, 16)
  if (Number.isNaN(num)) return null
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  }
}

const textColor = computed(() => {
  const rgb = hexToRgb(bgColor.value)
  if (!rgb) return '#FFFFFF'
  // YIQ 对比度判断，浅底用深色字，深底用白字
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  return yiq >= 160 ? '#1F2A37' : '#FFFFFF'
})
</script>

<style scoped lang="less">
.share-avatar-badge {
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid #d7dee9;
  flex-shrink: 0;

  .badge-text {
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    font-family: Arial, sans-serif;
    letter-spacing: 0.2px;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
    position: relative;
    z-index: 1;
    user-select: none;
  }

  .badge-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
